import { Box, Button, Card, CardContent, Chip, Stack, Typography } from '@mui/material';
import { ScheduleOutlined, SyncOutlined } from '@mui/icons-material';
import { LoadingButton } from '@mui/lab';
import { FunctionComponent, useContext, useEffect, useMemo, useState } from 'react';
import { ReportPeriodContext } from '../../../context/report-periods/report-periods';
import { UserNotificationsContext } from '../../../context/user-notifications/user-notifications';
import { TableDisplay } from '../../../components/presentation-components/miscellaneous-components/table-display';
import { FormSelectInputDisplay } from '../../../components/presentation-components/form-field-components/new-and-edit/form-select-input';
import { LoadingSpinner } from '../../../components/presentation-components/display-elements-components/loading-spinner';
import { ScheduleSyncDialog } from '../../../components/dialogs/settings/schedule-sync-dialog';
import { IHeadCell } from '../../../shared/interfaces/table-display.model';
import { formatEmissionAmount } from '../../../shared/utils/emission-summary-utils';
import CloudEmissionsService from '../shared-services/cloud-emissions/cloud-emissions.service';
import {
  CLOUD_PROVIDER_NAMES,
  CLOUD_PROVIDERS_WITH_BOTH_BASES,
  CloudScopeLabel,
  ICloudConnectionStatus,
  ICloudCoverageSummary,
  ICloudLine,
  ICloudRecordSummary,
  scopeNoun,
} from '../shared-services/cloud-emissions/cloud-emissions.model';
import { CLOUD_STATUS_CHIPS, formatMonthKey } from './cloud-emissions-format';
import { CloudMonthDialog } from './cloud-month-dialog';

const cardSx = { borderColor: 'lightGrey.main', borderRadius: '10px', boxShadow: 'none' };
const BOOKABLE = ['READY', 'RESTATED'];

/**
 * The vendor's months for one report period: sync, see what each month will do, open a month
 * to decide or to see where its carbon came from, and book the months that are ready.
 */
export const CloudMonthsPanel: FunctionComponent<{
  status: ICloudConnectionStatus,
  isAdmin: boolean,
  onStatusStale: () => void,
}> = ({ status, isAdmin, onStatusStale }) => {
  const cloudEmissionsService = new CloudEmissionsService();
  const reportPeriodContext = useContext(ReportPeriodContext);
  const userNotificationsContext = useContext(UserNotificationsContext);

  const periods = reportPeriodContext.allReportPeriods || [];
  const [reportPeriodId, setReportPeriodId] = useState<string>(reportPeriodContext.lastSelectedReportId || periods[0]?.id || '');
  const [lines, setLines] = useState<ICloudLine[]>([]);
  const [records, setRecords] = useState<Record<string, ICloudRecordSummary>>({});
  const [isLoading, setIsLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState('');
  const [isSyncing, setIsSyncing] = useState(false);
  const [isBooking, setIsBooking] = useState(false);
  const [coverage, setCoverage] = useState<ICloudCoverageSummary | null>(null);
  const [openLineId, setOpenLineId] = useState<string | null>(null);
  const [openSchedule, setOpenSchedule] = useState(false);
  const [selected, setSelected] = useState<string[]>([]);

  const selectedPeriod = periods.find(period => period.id === reportPeriodId);
  const periodLocked = !!selectedPeriod?.locked;
  const setupConfirmed = !!status.connection?.settings.supplierMappingConfirmedAt;

  const loadLines = async () => {
    if (!reportPeriodId) {
      setIsLoading(false);
      return;
    }
    try {
      setIsLoading(true);
      setErrorMessage('');
      const response = await cloudEmissionsService.getLines(status.provider, reportPeriodId);
      setLines(response.lines || []);
      setRecords(response.records || {});
    } catch (error) {
      console.error('Error loading cloud months:', error);
      setErrorMessage('The months could not be loaded. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (!reportPeriodId && periods[0]?.id) setReportPeriodId(reportPeriodContext.lastSelectedReportId || periods[0].id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [periods.length]);

  useEffect(() => {
    setCoverage(null);
    loadLines();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reportPeriodId, status]);

  const sync = () => {
    setIsSyncing(true);
    cloudEmissionsService.sync(status.provider, reportPeriodId)
      .then(result => {
        setCoverage(result.coverageSummary);
        userNotificationsContext.openSnackbar({ open: true, message: 'Sync complete', backgroundColor: 'primary.dark' });
        loadLines();
        onStatusStale();
      })
      .catch(err => userNotificationsContext.openSnackbar({
        open: true,
        message: err?.response?.data?.message || 'The sync could not be completed. Please try again.',
        backgroundColor: 'text.primary',
      }))
      .finally(() => setIsSyncing(false));
  };

  const bookable = lines.filter(line => BOOKABLE.includes(line.carbonStatus));

  const bookReady = async () => {
    const attachCount = bookable.filter(line => line.carbonStatus === 'READY' && line.carbonDetail.match?.action === 'ATTACH').length;
    const standaloneCount = bookable.filter(line => line.carbonStatus === 'READY' && line.carbonDetail.match?.action === 'STANDALONE').length;
    const restatedCount = bookable.filter(line => line.carbonStatus === 'RESTATED').length;
    const parts = [
      attachCount ? `${attachCount} will replace the spend estimate on their invoice record` : null,
      standaloneCount ? `${standaloneCount} will be booked as their own record because no invoice was found` : null,
      restatedCount ? `${restatedCount} will update a record to the vendor's restated figure` : null,
    ].filter(Boolean);
    const confirmed = await userNotificationsContext.openDialog({
      active: true,
      title: `Book ${bookable.length} ${bookable.length === 1 ? 'month' : 'months'}?`,
      message: `${parts.join('; ')}. Months needing a decision are left for you.`,
      buttonCopy: 'Book months',
      cancelButtonCopy: 'Cancel',
    });
    if (!confirmed) return;
    setIsBooking(true);
    try {
      const result = await cloudEmissionsService.commit(status.provider, bookable.map(line => line.id));
      const booked = result.committed.length + result.restated.length;
      const changed = result.skipped.filter(item => item.reason === 'DECISION_CHANGED').length;
      userNotificationsContext.openSnackbar({
        open: true,
        message: changed
          ? `${booked} booked. ${changed} changed since the last sync and now need another look.`
          : `${booked} ${booked === 1 ? 'month' : 'months'} booked`,
        backgroundColor: 'primary.dark',
      });
      onStatusStale();
      loadLines();
    } catch (err: any) {
      userNotificationsContext.openSnackbar({
        open: true,
        message: err?.response?.data?.message || 'The months could not be booked. Please try again.',
        backgroundColor: 'text.primary',
      });
    } finally {
      setIsBooking(false);
    }
  };

  const headCells: IHeadCell[] = [
    { id: 'month', label: 'Month', numeric: false, sortable: true, sortValue: 'monthSort' },
    { id: 'scope', label: scopeNoun(status.scopeLabel, { capitalised: true }), numeric: false, sortable: true },
    { id: 'figure', label: status.basis === 'MARKET' ? 'Market-based' : 'Location-based', numeric: true, sortable: true, sortValue: 'figureSort' },
    ...(CLOUD_PROVIDERS_WITH_BOTH_BASES.includes(status.provider)
      ? [{ id: 'other', label: status.basis === 'MARKET' ? 'Location-based' : 'Market-based', numeric: true, sortable: false } as IHeadCell]
      : []),
    { id: 'status', label: 'Status', numeric: false, sortable: true, sortValue: 'statusSort' },
  ];

  const rows = useMemo(() => lines.map(line => {
    const chip = CLOUD_STATUS_CHIPS[line.carbonStatus];
    return {
      id: line.id,
      month: formatMonthKey(line.carbonDetail.monthKey),
      monthSort: line.carbonDetail.monthKey,
      scope: line.carbonDetail.scopeName,
      figure: formatEmissionAmount(line.total),
      figureSort: line.total,
      other: line.carbonDetail.kgOtherBasis != null ? formatEmissionAmount(line.carbonDetail.kgOtherBasis) : '—',
      status: <Chip label={chip.label} color={chip.color} variant='outlined' size='small' />,
      statusSort: chip.label,
    };
  }), [lines]);

  const counts = lines.reduce<Record<string, number>>((acc, line) => {
    acc[line.carbonStatus] = (acc[line.carbonStatus] || 0) + 1;
    return acc;
  }, {});
  const openLine = lines.find(line => line.id === openLineId) || null;

  return (
    <Card variant='outlined' sx={cardSx}>
      <CardContent>
        <Stack spacing={3}>
          <Stack direction={{ xs: 'column', md: 'row' }} justifyContent='space-between' alignItems={{ xs: 'flex-start', md: 'center' }} gap={2}>
            <Stack spacing={1}>
              <Typography variant='h6' sx={{ color: 'text.primary' }}>Monthly figures</Typography>
              <Typography variant='body2' sx={{ color: 'text.secondary', maxWidth: '680px' }}>
                One figure per {status.scopeLabel} per month, booked to Scope 3 Category 1. Open a month to see
                where its carbon came from, or to decide where it belongs.
              </Typography>
            </Stack>
            {/* Flex gap rather than `spacing`: spacing puts a left margin on every button after
                the first, so a button that wraps keeps that margin and sits offset under the
                others with no gap above it. gap spaces rows and columns alike. */}
            <Stack direction='row' flexWrap='wrap' useFlexGap gap={2} alignItems='center'>
              {isAdmin && (
                <Button variant='outlined' startIcon={<ScheduleOutlined fontSize='small' />} onClick={() => setOpenSchedule(true)}>
                  <Typography sx={{ pl: 0.5 }}>Schedule sync</Typography>
                </Button>
              )}
              <LoadingButton
                variant='outlined'
                loading={isSyncing}
                disabled={!reportPeriodId || periodLocked}
                startIcon={<SyncOutlined fontSize='small' />}
                onClick={sync}
              >
                <Typography sx={{ pl: 0.5 }}>Sync now</Typography>
              </LoadingButton>
              <LoadingButton
                variant='contained'
                loading={isBooking}
                disabled={bookable.length === 0 || !setupConfirmed || periodLocked}
                onClick={bookReady}
              >
                <Typography>{bookable.length ? `Book ready months (${bookable.length})` : 'Book ready months'}</Typography>
              </LoadingButton>
            </Stack>
          </Stack>

          <FormSelectInputDisplay
            isEditPage={false}
            allowEdit={false}
            disabled={isSyncing || isBooking}
            isTopRow
            isBottomRow
            title='Report period'
            name='cloud-report-period'
            value={reportPeriodId}
            array={periods}
            valueKey='id'
            labelKey={(period: any) => `${period.friendlyName || period.calendarYear}${period.locked ? ' (locked)' : ''}`}
            onChange={(e: any) => setReportPeriodId(e.target.value)}
          />

          {!setupConfirmed && (
            <Typography variant='body2' sx={{ color: 'text.secondary' }}>
              You can sync now to see the figures. Nothing is booked until setup is confirmed.
            </Typography>
          )}

          {coverage && <CoverageSummary coverage={coverage} scopeLabel={status.scopeLabel} />}

          {Object.keys(counts).length > 0 && (
            <Stack direction='row' spacing={1} flexWrap='wrap' useFlexGap>
              {Object.entries(counts).map(([key, count]) => {
                const chip = CLOUD_STATUS_CHIPS[key as keyof typeof CLOUD_STATUS_CHIPS];
                return <Chip key={key} label={`${chip.label}: ${count}`} color={chip.color} variant='outlined' size='small' />;
              })}
            </Stack>
          )}

          {isLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '200px' }}>
              <LoadingSpinner />
            </Box>
          ) : errorMessage ? (
            <Stack spacing={1}>
              <Typography color='error'>{errorMessage}</Typography>
              <Button onClick={loadLines} sx={{ alignSelf: 'flex-start' }}>Try again</Button>
            </Stack>
          ) : lines.length === 0 ? (
            <Typography variant='body2' sx={{ color: 'text.secondary' }}>
              No months for this period yet. Sync to fetch the figures {status.vendorName} has published.
            </Typography>
          ) : (
            <TableDisplay
              headCells={headCells}
              rows={rows}
              selected={selected}
              setSelected={setSelected}
              perPage={25}
              onRowClick={(id: string) => setOpenLineId(id)}
              defaultOrderBy='monthSort'
              defaultOrder='desc'
            />
          )}
        </Stack>
      </CardContent>

      <CloudMonthDialog
        open={!!openLine}
        line={openLine}
        records={records}
        status={status}
        canAct={!periodLocked}
        onClose={() => setOpenLineId(null)}
        onChanged={() => { loadLines(); onStatusStale(); }}
      />
      {isAdmin && (
        <ScheduleSyncDialog
          open={openSchedule}
          onClose={() => setOpenSchedule(false)}
          provider={status.provider}
          providerName={CLOUD_PROVIDER_NAMES[status.provider]}
          actionType='sync_cloud_emissions'
        />
      )}
    </Card>
  );
};

const CoverageSummary: FunctionComponent<{ coverage: ICloudCoverageSummary, scopeLabel: CloudScopeLabel }> = ({ coverage, scopeLabel }) => {
  const lines: string[] = [];
  lines.push(`${coverage.monthsFound.length} ${coverage.monthsFound.length === 1 ? 'month' : 'months'} returned across ${coverage.scopesSynced} ${scopeNoun(scopeLabel, { plural: coverage.scopesSynced !== 1 })}: ${coverage.newLines || 0} new, ${coverage.updatedLines || 0} updated, ${coverage.restatedLines || 0} restated.`);
  if (coverage.monthsMissing.length) {
    const months = [...new Set(coverage.monthsMissing.map(m => formatMonthKey(m.monthKey)))];
    lines.push(`No figure returned for ${months.slice(0, 6).join(', ')}${months.length > 6 ? ` and ${months.length - 6} more` : ''}.`);
  }
  if (coverage.monthsNotYetPublished.length) {
    lines.push(`Not published yet: ${coverage.monthsNotYetPublished.map(formatMonthKey).join(', ')}.`);
  }
  if (coverage.monthsBeforeVendorHistory.length) {
    lines.push(`Before the vendor's history, so not available: ${coverage.monthsBeforeVendorHistory.map(formatMonthKey).join(', ')}.`);
  }
  if (coverage.deniedScopes.length) {
    lines.push(`Access refused for ${coverage.deniedScopes.length} ${scopeNoun(scopeLabel, { plural: coverage.deniedScopes.length !== 1 })}: ${coverage.deniedScopes[0].reason || 'no reason given'}.`);
  }
  if (coverage.breakdown.note) lines.push(coverage.breakdown.note);
  return (
    <Stack spacing={0.5} sx={{ p: 2, borderRadius: '10px', backgroundColor: 'lightGrey.light' }}>
      <Typography variant='subtitle2' sx={{ color: 'text.primary' }}>Last sync</Typography>
      {lines.map(text => (
        <Typography key={text} variant='body2' sx={{ color: 'text.secondary' }}>{text}</Typography>
      ))}
    </Stack>
  );
};
